<template>
  <div
    class="rf-bulk-action-menu border-beige-10 bg-white-1 flex items-center gap-2 overflow-hidden rounded-t-lg border border-b-0 border-solid pl-4"
    data-cy="table-select"
  >
    <RfCheckbox
      data-cy="table-select--all"
      class="inline-flex items-center justify-center"
      :modelValue="!!(selected.size && isAllSelected)"
      :intermediate="!!(selected.size && !isAllSelected)"
      @update:modelValue="emit(isAllSelected ? 'deselectAll' : 'selectAll')"
    />
    <RfMenu
      ref="tableSelect"
      class="[&_.rf-menu--button]:hover:bg-beige-1 [&_.rf-menu--opened]:bg-beige-1 h-full pr-1.5 [&_.rf-menu--button]:h-full [&_.rf-menu--button]:rounded-l-none! [&_.rf-menu--button]:rounded-r-sm"
    >
      <template #menu>
        <div class="list flex w-36 flex-col gap-4 p-4">
          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-all"
            :class="{ 'bg-beige-1! text-beige-90!': isAllSelected }"
            @click="() => (emit('selectAll'), $refs.tableSelect.hide())"
          >
            All
          </button>
          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-enabled"
            :class="{
              'bg-beige-1! text-beige-90!':
                !!selectedStatuses[Statuses.true.type] && !selectedStatuses[Statuses.false.type],
            }"
            @click="() => (emit('selectByPreset', Statuses.true.type), $refs.tableSelect.hide())"
          >
            {{ Statuses.true.title }}
          </button>

          <button
            class="rounded p-2 text-left"
            data-cy="table-select--dropdown-disabled"
            :class="{
              'bg-beige-1! text-beige-90!':
                !selectedStatuses[Statuses.true.type] && !!selectedStatuses[Statuses.false.type],
            }"
            @click="() => (emit('selectByPreset', Statuses.false.type), $refs.tableSelect.hide())"
          >
            {{ Statuses.false.title }}
          </button>
        </div>
      </template>
    </RfMenu>

    <Transition>
      <span
        v-if="selected.size"
        class="text-body text-grey-1 mr-2 inline-flex items-center gap-2"
        data-cy="table-select--actions"
      >
        <RfColorTag
          v-if="selected.size"
          class="rf-tags-group--tag"
          :title="`${selected.size} selected`"
          pill
          data-cy="table-select--selected"
          :data-cy-value="selected.size"
          backgroundColor="#FFF"
          @clear="emit('deselectAll')"
        />

        <RfButtonNew
          v-if="selected.size === 1"
          data-cy="table-select--edit"
          size="large"
          type="table"
          :disabled="selectedStatuses.disableStatusChange"
          @click="emit('edit')"
        >
          <RfEditIcon class="fill-beige-50 h-6 w-6" />
          Edit
        </RfButtonNew>

        <RfButtonNew
          v-if="!selectedStatuses[Statuses.true.type] && !!selectedStatuses[Statuses.false.type]"
          data-cy="table-select--enable"
          size="large"
          type="table"
          :disabled="selectedStatuses.disableStatusChange"
          @click="emit('status', Statuses.true.type)"
        >
          <RfPlayIcon class="fill-beige-50 h-6 w-6" />
          Enable
        </RfButtonNew>

        <RfButtonNew
          v-if="!!selectedStatuses[Statuses.true.type] && !selectedStatuses[Statuses.false.type]"
          data-cy="table-select--disable"
          size="large"
          type="table"
          :disabled="selectedStatuses.disableStatusChange"
          @click="emit('status', Statuses.false.type)"
        >
          <RfStopIcon class="fill-beige-50 h-6 w-6" />
          Disable
        </RfButtonNew>

        <RfButtonNew
          v-if="selected.size === 1"
          :disabled="
            selectedIsSystemSegment
              ? 'System segments cannot be cloned. Only one segment of this type is allowed per app.'
              : false
          "
          size="large"
          type="table"
          data-cy="table-select--clone"
          @click="emit('clone')"
        >
          <RfCloneIcon class="fill-beige-50 h-6 w-6" />
          Clone
        </RfButtonNew>

        <RfButtonNew size="large" type="table" @click="emit('export')">
          <RfDownloadIcon class="fill-beige-50 h-6 w-6" />
          Export to CSV
        </RfButtonNew>

        <RfButtonNew
          size="large"
          data-cy="table-select--delete"
          type="table"
          :disabled="selectedStatuses.disableStatusChange"
          @click="emit('delete')"
        >
          <RfDeleteIcon class="fill-beige-50 h-6 w-6" />
          Delete
        </RfButtonNew>
      </span>
    </Transition>
  </div>
</template>

<script setup>
import RfMenu from "@/components/menus/RfMenu.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfStopIcon from "@/components/icons/RfStopIcon.vue";
import RfPlayIcon from "@/components/icons/RfPlayIcon.vue";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import { computed } from "vue";
import { Statuses } from "@/utils/constants/SegmentsConstants";

const emit = defineEmits([
  "selectAll",
  "deselectAll",
  "selectByPreset",
  "edit",
  "status",
  "clone",
  "export",
  "delete",
]);

const props = defineProps({
  selected: { type: Map, default: null },
  isAllSelected: { type: Boolean, default: false },
  tableData: { type: Object, default: () => ({ page: 1, perPage: 10, totalCount: 10 }) },
});

const isAllSelected = computed(() => props.tableData.showing === props.selected.size);

const selectedStatuses = computed(() =>
  [...props.selected].reduce((acc, [_, segment]) => {
    const status = Statuses[segment.is_enabled].type;
    return {
      ...acc,
      [status]: +!!acc[status] + 1,
      disableStatusChange:
        acc.disableStatusChange || !!segment.is_locked || segment.segment_type === "pinpoint",
    };
  }, {}),
);

const selectedIsSystemSegment = computed(() => {
  if (props.selected.size !== 1) return false;
  const [segment] = [...props.selected.values()];
  return segment?.segment_type?.startsWith("system_");
});
</script>

<style scoped>
.rf-button.disabled {
  @apply opacity-50;
}
</style>
